import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import circleImg from '../../recursos/circle.png';
import { useHistory } from 'react-router-dom'; 
import { gsap } from 'gsap';

const ThreeScene = ({ onScrollDown }) => {
  const mountRef = useRef(null);
  const buttonRef = useRef(null);
  const history = useHistory();

  const handleNavigate = () => {
    // Animación de cortina (igual que en tus otros componentes)
    gsap.to(".curtain", {
      y: '-100%',
      duration: 2,
      ease: 'power2.inOut',
      onComplete: () => {
        history.push("/empatica-description"); // Navega después de la animación
      }
    });
  };

  useEffect(() => {
    // Configuración de Three.js
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.set(100, 10, 0);

    const renderer = new THREE.WebGLRenderer({
      alpha: true,
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    mountRef.current.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.03;

    // Código para crear los puntos
    const textureLoader = new THREE.TextureLoader();
    const imgTex = textureLoader.load(circleImg);

    const count = 100;
    const sep = 3;
    const positions = new Float32Array(count * count * 3);

    let t = 0;
    let f = 0.002;
    let a = 3;

    const graph = (x, z) => Math.sin(f * (x ** 2 + z ** 2 + t)) * a;

    let i = 0;
    for (let xi = 0; xi < count; xi++) {
      for (let zi = 0; zi < count; zi++) {
        let x = sep * (xi - count / 2);
        let z = sep * (zi - count / 2);
        let y = graph(x, z);
        positions[i] = x;
        positions[i + 1] = y;
        positions[i + 2] = z;
        i += 3;
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      map: imgTex,
      color: 0x00AAFF,
      size: 0.5,
      sizeAttenuation: true,
      transparent: false,
      alphaTest: 0.5,
      opacity: 1.0,
    });

    const points = new THREE.Points(geometry, material);
    scene.add(points);

    // Animación
    const animate = function () {
      requestAnimationFrame(animate);

      t += 15;
      let i = 0;
      for (let xi = 0; xi < count; xi++) {
        for (let zi = 0; zi < count; zi++) {
          let x = sep * (xi - count / 2);
          let z = sep * (zi - count / 2);
          positions[i + 1] = graph(x, z);
          i += 3;
        }
      }

      geometry.attributes.position.needsUpdate = true;
      controls.update();
      renderer.render(scene, camera);
    };

    animate();

    // Manejo del redimensionamiento
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
      if (mountRef.current) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []);

  // Estilos para el botón
  const buttonStyles = {
    position: 'absolute',
    bottom: '70px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.2)',
    border: '2px solid white',
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
    zIndex: 10,
    animation: 'bounce 2s infinite',
    outline: 'none',
  };

  // Estilos para el contenedor principal
  const containerStyles = {
    position: 'relative',
    width: '100%',
    height: '100%',
  };

  // Estilos para la animación (se inyectan en el head)
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes bounce {
        0%, 20%, 50%, 80%, 100% {
          transform: translateX(-50%) translateY(0);
        }
        40% {
          transform: translateX(-50%) translateY(-10px);
        }
        60% {
          transform: translateX(-50%) translateY(-5px);
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return (
    <div style={containerStyles}>
      <div ref={mountRef} style={{ width: '100%', height: '100%' }}></div>
      
      {/* Botón con flecha hacia abajo */}
      <button
        ref={buttonRef}
        style={buttonStyles}
        onClick={handleNavigate}
        aria-label="Desplazarse hacia abajo"
      >
        <svg 
          width="24" 
          height="24" 
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path 
            d="M7 10L12 15L17 10" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
};

export default ThreeScene;